<template>
  <!--
    A RODA (re-pele 03/09): a mecânica e a matemática do giro ficaram intactas — mudou a
    roupa. Aro de madeira parado (o antigo girava dourado, contra o D0), gomos na família da
    água, miolo de latão, e o ponteiro OSCILA de leve em repouso (D10-3: "o ponteiro da roleta
    oscila"). Tickets num bilhete de papel; a vitória é papel sem fogos — o som conta a festa.
  -->
  <div class="roulette-container">
    <p class="roda__tickets">
      <FishIcon nome="ticket-alt" tamanho="pequeno" />
      <span class="t-numero">{{ playerTickets }}</span> {{ $t('roulette.tickets') }}
    </p>

    <div class="roulette-wheel" :class="{ 'roulette-wheel--girando': isSpinning }">
      <div class="outer-ring"></div>
      <div class="inner-wheel" ref="wheel">
        <div v-for="(prize, index) in scaledPrizes"
             :key="index"
             class="number"
             :style="{
               '--i': index,
               '--clr': getPrizeColor(index),
               '--amtRwd': prizes.length,
               '--cpPhPg': getClipPath(prizes.length)
             }">
          <span>{{ prize.displayText }}</span>
        </div>
      </div>
      <div class="pointer"></div>
    </div>

    <div class="roda__acao">
      <FishButton
        variante="primario"
        tamanho="grande"
        bloco
        som=""
        :desabilitado="isSpinning || playerTickets <= 0"
        :motivo="playerTickets <= 0 ? $t('roulette.button.needTickets') : ''"
        @click="startSpin"
      >
        {{ $t('roulette.button.spin') }}
      </FishButton>
    </div>

    <transition name="fade">
      <div v-if="showVictory" class="victory-modal">
        <div class="roda__premio papel-empilhado subir-surgindo">
          <FishIcon nome="gift" tamanho="grande" />
          <h2>{{ $t('roulette.victory.title') }}</h2>
          <p>{{ $t('roulette.victory.won') }}{{ formatPrizeText(lastWonPrize) }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import rouletteService from '@/services/rouletteService';
import { FishButton, FishIcon } from '@/shared/ui';

/** O tipo de prêmio do servidor → a chave de texto da roda. */
const TIPO_NA_TELA = {
  stamina: 'energy',
  moreStamina: 'energy',
  gold: 'coins',
  moreGold: 'coins',
  bait: 'bait',
  moreBait: 'bait',
  paralleliums: 'paralleliums',
  experience: 'experience'
};

export default {
  name: 'RouletteWheel',
  components: { FishButton, FishIcon },

  data() {
    return {
      spinDuration: 4000, // Duration in ms
      prizes: [], // Will be filled from backend
      isSpinning: false,
      showVictory: false,
      lastWonPrize: null,
      rotations: 5, // Number of full rotations before stopping
      baseAngle: 0, // Add this to track current wheel position
      debugMode: true, // Add this for debugging
    }
  },

  computed: {
    ...mapState({
      playerTickets: state => state.player.tickets,
      playerId: state => state.player.id
    }),
    
    scaledPrizes() {
      return this.prizes.map(prize => ({
        ...prize,
        displayText: this.formatPrizeText(prize)
      }));
    }
  },

  async created() {
    await this.loadPrizes();
  },

  methods: {
    async loadPrizes() {
      try {
        // O V2 devolve a lista pronta (`[{ id, name, type, value }]`), com o
        // `value` já no nível do jogador. Sem cache no store: o valor muda
        // quando o jogador sobe de nível.
        this.prizes = await rouletteService.getPrizes(this.playerId);
      } catch (error) {
        console.error(
          'Failed to load prizes:',
          error.response?.data?.message || error.response?.data?.error?.message || error.message,
        );
      }
    },

    getSegmentStyle(index) {
      const segmentAngle = 360 / this.prizes.length;
      return {
        transform: `rotate(${segmentAngle * index}deg) skewY(${90 - segmentAngle}deg)`,
        background: this.getPrizeColor(index)
      }
    },

    getContentStyle() {
      const segmentAngle = 360 / this.prizes.length;
      return {
        transform: `skewY(-${90 - segmentAngle}deg)`
      }
    },

    getPrizeColor(index) {
      // A família da ÁGUA do jogo, em quatro profundidades — os azuis elétricos antigos eram de
      // outra paleta. Quatro tons e não dois: com contagem ímpar de gomos, dois tons colariam o
      // primeiro no último.
      const colors = [
        '#1d4557', // raso
        '#2a6b7d', // meia-água
        '#163646', // fundo
        '#33758f'  // corrente
      ];
      // A lista de prêmios vem do servidor e pode ter mais de 8 itens — o
      // módulo evita segmento sem cor (fundo transparente) nesse caso.
      return colors[index % colors.length];
    },

    getClipPath() {
      // For 8 segments specifically
      return 63; // This value is optimized for 8 segments
    },

    /**
     * O texto do prêmio, a partir do tipo e do valor **que o servidor mandou**.
     * Até 23/09/2026 a tela multiplicava pelo nível por conta própria e o
     * servidor não: a roda dizia "+30 Energia" e o crédito era 15.
     */
    formatPrizeText(prize) {
      if (!prize) return '';
      const chave = TIPO_NA_TELA[prize.type];
      return chave ? this.$t(`roulette.prizes.${chave}`, { n: prize.value }) : prize.name;
    },
    
    async startSpin() {
      if (this.isSpinning || this.playerTickets <= 0) return;

      try {
        this.isSpinning = true;
        await this.$store.commit('player/REMOVE_TICKET');
        await this.$store.dispatch('SoundController/playSound', { 
          category: 'roulette', 
          type: 'spin' 
        });

        // O spin já credita o prêmio no servidor — o resultado volta fechado
        // (`{ name, type, value }`) e a animação é só teatro sobre ele.
        const winningPrize = await rouletteService.spinRoulette(this.playerId);

        // Find the exact index of the winning prize
        const prizeIndex = this.prizes.findIndex(p => p.id === winningPrize.id);

        if (prizeIndex === -1) {
          console.error('Prize not found in wheel:', winningPrize.name);
          throw new Error('Invalid prize result');
        }

        // Calculate rotation for exact prize position
        const segmentAngle = 360 / this.prizes.length;
        const currentRotation = this.baseAngle % 360;
        
        // Calculate target angle to point to the winning segment
        // Note: We multiply by -1 because we want clockwise rotation
        let targetAngle = -(segmentAngle * (prizeIndex - 1));
        
        // Normalize angles to 0-360 range
        const normalizedTarget = ((targetAngle % 360) + 360) % 360;
        const normalizedCurrent = ((currentRotation % 360) + 360) % 360;
        
        // Calculate shortest path
        let angleDiff = normalizedTarget - normalizedCurrent;
        if (Math.abs(angleDiff) > 180) {
          angleDiff = angleDiff > 0 ? angleDiff - 360 : angleDiff + 360;
        }

        // Final angle calculation:
        // 1. Start from current position (this.baseAngle)
        // 2. Add full rotations
        // 3. Add angle difference to reach target
        const fullRotations = 360 * this.rotations;
        const finalAngle = this.baseAngle + fullRotations + angleDiff;

        // Update base angle for next spin
        this.baseAngle = finalAngle;

        // Animate wheel
        const wheel = this.$refs.wheel;
        wheel.style.transition = `transform ${this.spinDuration}ms cubic-bezier(0.32, 0.64, 0.45, 1)`;
        wheel.style.transform = `rotate(${finalAngle}deg)`;

        // Handle winning
        setTimeout(async () => {
          this.isSpinning = false;
          this.lastWonPrize = winningPrize;
          this.showVictory = true;

          await this.$store.dispatch('SoundController/playSound', { 
            category: 'roulette', 
            type: 'win' 
          });

          // Não existe mais `claim`: o crédito aconteceu dentro do spin.
          // Recarregar o jogador basta para o saldo/ticket aparecerem certos.
          await this.$store.dispatch('player/loadPlayerState', this.playerId);

          setTimeout(() => {
            this.showVictory = false;
          }, 3000);
        }, this.spinDuration);

      } catch (error) {
        console.error('Error spinning roulette:', error);
        this.isSpinning = false;
        await this.$store.commit('player/ADD_TICKET');
      }
    }
  }
}
</script>

<style scoped>
.roulette-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--e-3);
}

/* O bilhete dos tickets: papel com o fio de latão — a mesma língua dos avisos. */
.roda__tickets {
  margin: 0 0 var(--e-3);
  display: inline-flex;
  align-items: center;
  gap: var(--e-2);
  background: var(--papel);
  color: var(--tinta);
  border-left: 4px solid var(--latao);
  padding: var(--e-2) var(--e-3);
  font-size: var(--f-2);
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.35);
}

.roda__tickets .t-numero {
  color: var(--latao);
  font-size: var(--f-4);
}

.roulette-wheel {
  position: relative;
  width: 400px;
  height: 400px;
  margin: var(--e-4) 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* O ARO: madeira segmentada, PARADA — o antigo girava em ouro brilhante, contra o D0. */
.outer-ring {
  position: absolute;
  width: 112%;
  height: 112%;
  top: -6%;
  left: -6%;
  border-radius: 50%;
  background: repeating-conic-gradient(
    from 0deg,
    var(--madeira) 0deg 20deg,
    var(--madeira-sombra) 20deg 40deg
  );
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.4);
}

.outer-ring::before {
  content: '';
  position: absolute;
  width: 94%;
  height: 94%;
  top: 3%;
  left: 3%;
  border-radius: 50%;
  background: var(--fundo-encaixe, #071a26);
  box-shadow: inset 0 0 24px rgba(0, 0, 0, 0.5);
}

.inner-wheel {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #163646;
  overflow: hidden;
  transition: transform 5s cubic-bezier(0.32, 0.64, 0.45, 1);
  /* Dois filetes: latão por dentro, sombra por fora — sem glow. */
  box-shadow:
    0 0 0 4px var(--latao),
    0 0 0 8px rgba(0, 0, 0, 0.35);
}

/* O miolo: o botão de latão que prende a roda. */
.inner-wheel::after {
  content: '';
  position: absolute;
  width: 22%;
  height: 22%;
  top: 39%;
  left: 39%;
  border-radius: 50%;
  background: radial-gradient(circle at 32% 30%, var(--ouro-claro, #ffd76a), var(--latao));
  box-shadow: inset 0 -3px 0 rgba(0, 0, 0, 0.35);
}

.inner-wheel .number {
  position: absolute;
  width: 50%;
  height: 50%;
  transform-origin: bottom right;
  transform: rotate(calc(360deg / var(--amtRwd) * var(--i)));
  clip-path: polygon(0 0, 63% 0, 100% 100%, 0 63%);
  background: var(--clr);
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.inner-wheel .number span {
  position: relative;
  transform: rotate(calc(360deg / var(--amtRwd) / 2));
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--texto, #fff);
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.45);
  width: 100px;
  text-align: center;
}

/* O PONTEIRO: latão, e em repouso ele OSCILA de leve (D10-3). Girando, fica firme. */
.pointer {
  position: absolute;
  top: -30px;
  left: 50%;
  width: 44px;
  height: 56px;
  margin-left: -22px;
  transform-origin: 50% 20%;
  animation: ponteiro-oscilar 3.2s ease-in-out infinite;
  filter: drop-shadow(0 3px 0 rgba(0, 0, 0, 0.4));
}

.roulette-wheel--girando .pointer {
  animation: none;
}

.pointer::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, var(--ouro-claro, #ffd76a), var(--latao));
  clip-path: polygon(0% 0%, 100% 0%, 50% 100%);
}

.roda__acao {
  width: min(320px, 100%);
  margin-top: var(--e-3);
}

/* A vitória: papel, sem fogos — o som de vitória conta a festa (D0). */
.victory-modal {
  position: fixed;
  inset: 0;
  background: var(--veu, rgba(10, 20, 28, 0.65));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal, 500);
}

.roda__premio {
  --folha: 360px;
  width: min(360px, calc(100% - 2 * var(--e-3)));
  text-align: center;
}

.roda__premio h2 {
  margin: var(--e-2) 0;
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  color: var(--tinta);
}

.roda__premio p {
  margin: 0;
  font-size: var(--f-3);
  color: var(--tinta-fraca);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--t-medio) var(--curva-suave);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes ponteiro-oscilar {
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
}

@media (prefers-reduced-motion: reduce) {
  .pointer {
    animation: none;
  }
}

@media (max-width: 600px) {
  .roulette-wheel {
    width: 300px;
    height: 300px;
  }
}
</style>
